import { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '@@common';
import { ContextClient as Client, NewContextModel, SubDomainContextsModel, NewSubDomainModel, ContextModel } from '../../../api/api';

export const useContextController = () => {
	const client = useApiClient(Client);
	const [subDomains, setSubDomains] = useState<SubDomainContextsModel[]>([]);
	const [loadingSubDomains, setLoadingSubDomains] = useState(true);
	const [contexts, setContexts] = useState<ContextModel[]>([]);
	const [loadingContexts, setLoadingContexts] = useState(true);

	const loadSubDomains = useCallback(async () => {
		setLoadingSubDomains(true);
		try {
			const subDomainsData = await client.getSubDomains();
			setSubDomains(subDomainsData);
		} catch (error) {
			console.error('Failed to load sub domains:', error);
		} finally {
			setLoadingSubDomains(false);
		}
	}, [client]);

	const loadContexts = useCallback(async () => {
		try 
		{
			setLoadingContexts(true);
			// Example: Fetch contexts from API
			const contextsData = await client.getAllContexts();
			setContexts(contextsData);
		}
		catch (error) {
			console.error('Failed to load contexts:', error);
		}
		finally {
			setLoadingContexts(false);
		}
		// Implement loading contexts logic here
	}, [client]);

	useEffect(() => {
		loadSubDomains();
	}, [loadSubDomains]);

	const getContextById = useCallback(
		async (contextId: string) => {
			try {
				const context = await client.getContextById(contextId);
				console.log('Loaded Context', contextId, context);
				return context;
			} catch (error) {
				console.error('Failed to get context:', error);
			}
		},
		[client]
	);

	const createContext = useCallback(
		async (subDomainId: string, name: string, description: string) => {
			try {
				var newRequest = {
					description: description,
					contextName: name,
				} as NewContextModel;
				await client.createContext(subDomainId ?? '', newRequest); // ← adjust to your method name

				//Refresh
				await loadSubDomains();

				console.log('Context added!');
				// Optional: trigger a refresh or notify success
			} catch (error) {
				console.error('Failed to add new context:', error);
				// Optional: show error to user
			}
		},
		[client, loadSubDomains]
	);

	const createSubDomain = useCallback(
		async (name: string, description: string) => {
			try {
				var newRequest = {
					subDomainDescription: description,
					subDomainName: name,
				} as NewSubDomainModel;
				await client.createSubDomain(newRequest); // ← adjust to your method name
				//Refresh
				await loadSubDomains();
				console.log('SubDomain added!');
			} catch (error) {
				console.error('Failed to add new subDomain:', error);
				// Optional: show error to user
			}
		},
		[client, loadSubDomains]
	);

	const deleteContext = useCallback(
		async (contextId: string) => {
			try {
				await client.delete(contextId);
				// Refresh the subdomain list after deletion
				await loadSubDomains();
				console.log('Context deleted!');
			} catch (error) {
				console.error('Failed to delete context:', error);
				throw error; // Re-throw so the UI can handle the error
			}
		},
		[client, loadSubDomains]
	);

	return {
		subDomains,
		loadingSubDomains,
		createContext,
		getContextById,
		createSubDomain,
		deleteContext,
		loadContexts,
		contexts,
		loadingContexts,
	};
};
